<template>
  <b-container
    class="diagnostics-configurations"
    fluid
  >
    <diagnostics-filter
      class="mb-4"
      @search="search"
    />
    <b-row>
      <b-col
        class="mb-2"
        sm="4"
      >
        <b-checkbox
          :checked="areAllDiagnosticsSelected"
          :disabled="!shouldShowDiagnostics"
          @change="markAllDiagnostics"
        >
          <span class="font-weight-normal">
            {{ markAllCheckboxTitle(areAllDiagnosticsSelected) }}
          </span>
        </b-checkbox>
      </b-col>
      <b-col
        class="mb-2 text-center"
        sm="4"
      >
        <list-aggregator
          modal-item-id-key="id"
          modal-item-name-key="name"
          configuration-mode
          configuration-label="diagnostics-configurations.diagnostics-selected"
          :modal-table-items.sync="selectedDiagnostics"
          :modal-title="$t('diagnostics-configurations.configure-diagnostics')"
          :modal-table-header="$t('diagnostics-configurations.diagnostics')"
        />
      </b-col>
      <b-col
        class="mb-4"
        sm="4"
      >
        <s-search-input
          v-model="searchTerm"
          v-s-trigger="searchWithTerm"
          :placeholder="$t('diagnostics-configurations.search-diagnosis')"
        />
      </b-col>
    </b-row>

    <s-loader
      v-show="loading"
      size="lg"
    />

    <s-empty-list v-if="emptyHandler" />

    <div v-if="shouldShowDiagnostics">
      <b-tabs
        v-show="!loading"
        v-model="tabIndex"
      >
        <b-tab
          v-for="(tab, index) in diagnostics"
          :key="index"
          :title="$t(`diagnostics-configurations.modules.${tab.name}`)"
        >
          <accordion-list
            v-if="(tab.norms || []).length"
            :norms="tab.norms"
            :fixed-tab-title="tab.tabTitle"
            :module="tab.name"
            :selected-diagnostics.sync="selectedDiagnostics"
            :filter="filter"
            @mark-all-from-norm="markAllDiagnosticsFromNorm"
            @mark-all-from-method="markAllDiagnosticsFromMethod"
            @mark-diagnosis="markDiagnosis"
          />
          <div
            v-else-if="isEmptyList(tab)"
            class="diagnostics-configurations__empty-wrapper"
          >
            <s-empty-list
              :empty-message="$t('messages.MA076.message')"
              :loading="loading"
            />
          </div>
          <div
            v-else
            class="diagnostics-configurations__empty-wrapper"
          >
            <i
              class="diagnostics-configurations__empty-icon fas fa-ban"
              aria-hidden="true"
            />
            <label>
              {{ $t('diagnostics-configurations.classification-not-available') }}
            </label>
            <div class="diagnostics-configurations__empty-message">
              {{ $t('diagnostics-configurations.no-classification-message') }}
            </div>
          </div>
        </b-tab>
      </b-tabs>
      <div class="diagnostics-configurations__edit-btn-wrapper">
        <b-button
          variant="primary"
          :disabled="dontHaveSelectedDiagnostics"
          @click="edit"
        >
          {{ $t('actions.edit') }}
        </b-button>
        <diagnostic-configuration-modal
          v-model="showEditModal"
          :diagnostic-classification.sync="diagnosticClassification"
          :generates-alarm.sync="generatesAlarm"
          :selected-diagnostics="selectedDiagnostics"
          :payload="payloadHandler"
          @update-classifications="updateClassifications(filter)"
        />
      </div>
    </div>
  </b-container>
</template>

<script>
import uniqBy from 'lodash/uniqBy';
import uniq from 'lodash/uniq';
import { Modules } from 'sigma-mappings';
import DiagnosticsFilter from '@/domain/components/generalPanel/adminPanel/diagnosticsConfigurations/DiagnosticsFilter';
import DiagnosticsService from '@/domain/services/generalPanel/adminPanel/diagnosticsConfigurations/diagnosticsConfigurations';
import ListAggregator from '@/components/listAggregator';
import AccordionList from '@/domain/components/generalPanel/adminPanel/diagnosticsConfigurations/AccordionList';
import LoadingMixin from '@/services/loadingMixin';
import DiagnosticConfigurationModal from '@/domain/components/generalPanel/adminPanel/diagnosticsConfigurations/DiagnosticConfigurationModal';
import { isSameArray } from '@/services/arrayUtils';
import GlobalUserService from '@/services/user';

export default {
  name: 'DiagnosticsConfigurations',
  components: {
    AccordionList,
    ListAggregator,
    DiagnosticsFilter,
    DiagnosticConfigurationModal,
  },
  mixins: [LoadingMixin],
  data: () => ({
    filter: {
      classification: null,
      diagnosticsIds: [],
      generatesAlarm: null,
      module: null,
      norm: null,
      targetAssets: {},
    },
    diagnostics: [
      {
        norms: [],
        name: Modules.CHROMATOGRAPHY,
        emptyList: false,
      },
      {
        norms: [],
        name: Modules.PHYSICAL_CHEMICAL,
        emptyList: false,
      },
      {
        norms: [],
        name: Modules.OSCILLOGRAPHY_ANALYSIS,
        emptyList: false,
        tabTitle: 'diagnostics-configurations.modules.groups.oscillography-analysis',
      },
    ],
    diagnosticClassification: null,
    generatesAlarm: false,
    selectedDiagnostics: [],
    isAllDiagnosticsSelected: false,
    searchTerm: '',
    tabIndex: 0,
    showEditModal: false,
    amountAssetsAffected: 0,
  }),
  computed: {
    emptyHandler() {
      return !this.shouldShowDiagnostics && !this.loading;
    },
    shouldShowDiagnostics() {
      return !this.loading
        && this.diagnostics
        && ((this.chromatographyModel.norms || []).length
          || (this.physicalChemicalModel.norms || []).length
          || (this.oscillographyAnalysisModel.norms || []).length);
    },
    selectedDiagnosticsIds() {
      return this.selectedDiagnostics.map(diagnostic => diagnostic.id);
    },
    allDiagnostics() {
      const allDiagnostics = [];
      this.diagnostics.forEach((diagnosticModule) => {
        if (diagnosticModule.name === Modules.CHROMATOGRAPHY) {
          (diagnosticModule.norms || [])
            .forEach(norm => norm.methods
              .forEach(method => method.diagnostics
                .forEach(diagnostic => allDiagnostics.push(diagnostic))));
        }
        if (diagnosticModule.name === Modules.PHYSICAL_CHEMICAL) {
          (diagnosticModule.norms || [])
            .forEach(norm => norm.diagnostics
              .forEach(diagnostic => allDiagnostics.push(diagnostic)));
        }
        if (diagnosticModule.name === Modules.OSCILLOGRAPHY_ANALYSIS) {
          (diagnosticModule.norms || [])
            .forEach(norm => norm.diagnostics
              .forEach(diagnostic => allDiagnostics.push(diagnostic)));
        }
      });
      return allDiagnostics;
    },
    areAllDiagnosticsSelected() {
      return this.allDiagnostics.length === this.selectedDiagnostics.length;
    },
    dontHaveSelectedDiagnostics() {
      return this.selectedDiagnostics && !this.selectedDiagnostics.length;
    },
    oscillographyAnalysisDiagnosticsIds() {
      return (this.oscillographyAnalysisModel.norms || [])
        .reduce((ids, norm) => ids.concat((norm.diagnostics || []).map(({ id }) => id)), []);
    },
    selectedOscillographyAnalysisIds() {
      return uniq(this.selectedDiagnosticsIds)
        .filter(id => this.oscillographyAnalysisDiagnosticsIds.includes(id));
    },
    selectedChromaIds() {
      return uniq(this.selectedDiagnosticsIds)
        .filter(id => !this.oscillographyAnalysisDiagnosticsIds.includes(id));
    },
    payloadHandler() {
      const { targetAssets } = this.filter;
      return {
        companiesIds: (targetAssets || {}).companiesIds,
        regionalIds: (targetAssets || {}).regionalIds,
        installationsIds: (targetAssets || {}).installationsIds,
        assetsIds: (targetAssets || {}).assetsIds,
        diagnosticsIds: uniq(this.selectedDiagnosticsIds),
        chromaDiagnosticsIds: this.selectedChromaIds,
        oscillographyDiagnosticsIds: this.selectedOscillographyAnalysisIds,
        diagnosticClassification: this.diagnosticClassification,
        generatesAlarm: this.generatesAlarm,
        amountAssetsAffected: this.amountAssetsAffected,
      };
    },
    chromatographyModel: {
      get() {
        return this.diagnostics[0] || {};
      },
      set(value) {
        if ((this.diagnostics || []).length) {
          this.diagnostics[0] = value;
          return;
        }

        this.diagnostics = [value];
      },
    },
    physicalChemicalModel: {
      get() {
        return this.diagnostics[1] || {};
      },
      set(value) {
        if ((this.diagnostics || []).length > 1) {
          this.diagnostics[1] = value;
          return;
        }

        this.diagnostics = [null, value];
      },
    },
    oscillographyAnalysisModel: {
      get() {
        return this.diagnostics[2] || {};
      },
      set(value) {
        if ((this.diagnostics || []).length > 2) {
          this.diagnostics[2] = value;
          return;
        }

        this.diagnostics = [null, null, value];
      },
    },
  },
  created() {
    GlobalUserService.validateUserPermission('PER019');
    this.getDiagnostics(this.filter);
  },
  methods: {
    async updateClassifications(filter) {
      await this.getOnlyOrDefaultDiagnostics(filter);
      this.updateSelectedDiagnostics();
    },
    updateSelectedDiagnostics() {
      this.selectedDiagnostics = this.selectedDiagnostics
        .map(diagnostic => this.allDiagnostics.find(
          updatedDiagnostic => updatedDiagnostic.id === diagnostic.id,
        ))
        .filter(Boolean);
    },
    isEmptyList(tab = {}) {
      return tab.norms && !tab.norms.length && tab.emptyList;
    },
    searchWithTerm(term) {
      const payload = {
        ...this.filter,
        genericSearch: term,
      };
      this.getDiagnostics(payload);
    },
    showEmptyResponseMessage() {
      if (!(this.chromatographyModel.norms || []).length
        && !(this.physicalChemicalModel.norms || []).length
        && !(this.oscillographyAnalysisModel.norms || []).length) {
        this.$pushAlert({
          code: 'MA009',
        });
      }
    },
    async getAsyncClassifications(filter) {
      this.chromatographyModel.emptyList = false;
      this.physicalChemicalModel.emptyList = false;
      this.oscillographyAnalysisModel.emptyList = false;

      await Promise.allSettled([
        DiagnosticsService.getChromatographyClassifications(filter),
        DiagnosticsService.getPhysicalChemicalClassifications(filter),
        DiagnosticsService.getOscillographyAnalysisClassifications(filter)])
        .then((response) => {
          const hasChromatographyPermission = response[0].status !== 'rejected';
          this.chromatographyModel.norms = hasChromatographyPermission
            ? response[0].value.norms
            : [];

          this.chromatographyModel.emptyList = hasChromatographyPermission
            && !(response[0].value.norms || []).length;

          const hasPhysicalChemicalPermission = response[1].status !== 'rejected';
          this.physicalChemicalModel.norms = hasPhysicalChemicalPermission
            ? response[1].value.norms
            : [];

          this.physicalChemicalModel.emptyList = hasPhysicalChemicalPermission
            && !(response[1].value.norms || []).length;

          const hasOscillographyAnalysisPermission = response[2].status !== 'rejected';
          this.oscillographyAnalysisModel.norms = hasOscillographyAnalysisPermission
            ? response[2].value.norms
            : [];

          this.oscillographyAnalysisModel.emptyList = hasOscillographyAnalysisPermission
            && !(response[2].value.norms || []).length;

          const amountChromatography = hasChromatographyPermission
            ? response[0].value.amountAssetsAffected
            : 0;

          const amountPhysicalChemical = hasPhysicalChemicalPermission
            ? response[1].value.amountAssetsAffected
            : 0;

          const amountOscillographyAnalysis = hasOscillographyAnalysisPermission
            ? response[2].value.amountAssetsAffected : 0;

          this.amountAssetsAffected = Math.max(
            amountChromatography,
            amountPhysicalChemical,
            amountOscillographyAnalysis,
          );

          this.showEmptyResponseMessage();
        }).finally(() => {
          this.endLoading();
          this.updateSelectedDiagnostics();
        });
    },
    getChromatographyClassifications(filter) {
      this.physicalChemicalModel.norms = [];
      this.physicalChemicalModel.emptyList = true;
      this.oscillographyAnalysisModel.norms = [];
      this.oscillographyAnalysisModel.emptyList = true;

      return DiagnosticsService.getChromatographyClassifications(filter).then((response) => {
        this.amountAssetsAffected = response.amountAssetsAffected;
        this.chromatographyModel.norms = response.norms;
        this.chromatographyModel.emptyList = false;

        this.showEmptyResponseMessage();
      }).finally(() => {
        this.endLoading();
        this.updateSelectedDiagnostics();
      });
    },
    getPhysicalChemicalClassifications(filter) {
      this.chromatographyModel.norms = [];
      this.chromatographyModel.emptyList = true;
      this.oscillographyAnalysisModel.norms = [];
      this.oscillographyAnalysisModel.emptyList = true;

      return DiagnosticsService.getPhysicalChemicalClassifications(filter).then((response) => {
        this.amountAssetsAffected = response.amountAssetsAffected;
        this.physicalChemicalModel.norms = response.norms;
        this.physicalChemicalModel.emptyList = false;

        this.showEmptyResponseMessage();
      }).finally(() => {
        this.endLoading();
        this.updateSelectedDiagnostics();
      });
    },
    getOscillographyAnalysisClassifications(filter) {
      this.chromatographyModel.norms = [];
      this.chromatographyModel.emptyList = true;
      this.physicalChemicalModel.norms = [];
      this.physicalChemicalModel.emptyList = true;

      return DiagnosticsService.getOscillographyAnalysisClassifications(filter)
        .then((response) => {
          this.amountAssetsAffected = response.amountAssetsAffected;
          this.oscillographyAnalysisModel.norms = response.norms;
          this.oscillographyAnalysisModel.emptyList = false;

          this.showEmptyResponseMessage();
        }).finally(() => {
          this.endLoading();
          this.updateSelectedDiagnostics();
      });
    },
    async getOnlyOrDefaultDiagnostics(filterParams) {
      const filter = { ...filterParams };
      const numberModulesWithPermission = this.diagnostics.filter(
        diagnostic => diagnostic.norms.length > 0,
      );

      if (numberModulesWithPermission.length === 1) {
        filter.module = numberModulesWithPermission[0].name;
      }
      await this.getDiagnostics(filter);
    },
    getDiagnostics(filter = {}) {
      this.filter = filter;

      this.startLoading();
      if (filter.module === null) {
        return this.getAsyncClassifications(filter);
      }
      if (filter.module === Modules.CHROMATOGRAPHY) {
        return this.getChromatographyClassifications(filter);
      }
      if (filter.module === Modules.OSCILLOGRAPHY_ANALYSIS) {
        return this.getOscillographyAnalysisClassifications(filter);
      }
      return this.getPhysicalChemicalClassifications(filter);
    },
    markAllCheckboxTitle(state) {
      return state ? this.$t('diagnostics-configurations.unmark-all') : this.$t('diagnostics-configurations.mark-all');
    },
    markAllDiagnostics(state) {
      if (state) {
        (this.diagnostics || []).forEach(diagnostic => (diagnostic.norms || [])
          .forEach(norm => this.markAllDiagnosticsFromNorm(!this.isAllDiagnosticsSelected, norm)));
        return;
      }
      (this.diagnostics || []).forEach(diagnostic => (diagnostic.norms || [])
        .forEach(norm => this.markAllDiagnosticsFromNorm(state, norm)));
    },
    markAllDiagnosticsFromNorm(state, norm) {
      let normDiagnostics = [];
      if (norm.diagnostics && norm.diagnostics.length) {
        (norm.diagnostics || []).forEach((diagnostic) => {
          normDiagnostics = normDiagnostics.concat(diagnostic);
        });
      }
      (norm.methods || []).forEach((method) => {
        normDiagnostics = normDiagnostics.concat(method.diagnostics);
      });
      if (state) {
        const diagnosticsSelectedAndFromNorm = this.selectedDiagnostics.concat(
          normDiagnostics,
        );
        this.selectedDiagnostics = uniqBy(diagnosticsSelectedAndFromNorm, 'id');
        return;
      }
      const normDiagnosticsIds = normDiagnostics.map(({ id }) => id);
      this.selectedDiagnostics = this.selectedDiagnostics.filter(
        item => !normDiagnosticsIds.includes(item.id),
      );
    },
    markAllDiagnosticsFromMethod(state, method) {
      if (state) {
        const diagnosticsSelectedAndFromMethod = this.selectedDiagnostics.concat(
          method.diagnostics,
        );
        this.selectedDiagnostics = uniqBy(diagnosticsSelectedAndFromMethod, 'id');
        return;
      }
      const methodsDiagnosticsIds = method.diagnostics.map(({ id }) => id);
      this.selectedDiagnostics = this.selectedDiagnostics.filter(
        item => !methodsDiagnosticsIds.includes(item.id),
      );
    },
    isDiagnosisSelected(id) {
      return !!this.selectedDiagnostics.find(item => item.id === id);
    },
    markDiagnosis(state, diagnosis) {
      if (state && !this.isDiagnosisSelected(diagnosis.id)) {
        this.selectedDiagnostics.push(diagnosis);
        return;
      }
      if (!state && this.isDiagnosisSelected(diagnosis.id)) {
        this.selectedDiagnostics = this.selectedDiagnostics.filter(
          item => item.id !== diagnosis.id,
        );
      }
    },
    search(filter) {
      this.getDiagnostics(filter);
    },
    areAllSelectedDiagnosticsTheSameClassification(firstClassification) {
      return this.selectedDiagnostics
        .every(diagnostic => diagnostic.classifications && diagnostic.classifications
          .every(classification => classification === firstClassification));
    },
    setDiagnosticClassificationBySelectedDiagnostics() {
      const firstSelectedDiagnostic = this.selectedDiagnostics[0] || {};
      const firstSelectedDiagnosticClassifications = firstSelectedDiagnostic.classifications || [];
      const firstClassification = firstSelectedDiagnosticClassifications[0];

      this.diagnosticClassification = this.areAllSelectedDiagnosticsTheSameClassification(
        firstClassification,
      )
        ? firstClassification
        : null;
    },
    setGeneratesAlarmBySelectedDiagnostics() {
      const firstSelectedDiagnostic = this.selectedDiagnostics[0] || { alarms: [] };
      this.generatesAlarm = isSameArray(this.selectedDiagnostics, 'alarms')
        ? !!firstSelectedDiagnostic.alarms[0]
        : true;
    },
    setModalInformation() {
      this.setGeneratesAlarmBySelectedDiagnostics();
      this.setDiagnosticClassificationBySelectedDiagnostics();
    },
    edit() {
      this.setModalInformation();
      this.showEditModal = true;
    },
  },
};
</script>

<style lang="sass" scoped>
  .diagnostics-configurations
    &__empty-icon
      font-size: 50px
      margin-bottom: 10px
    &__empty-message
      max-width: 350px
      text-align: center
    &__empty-wrapper
      display: flex
      flex-direction: column
      align-items: center
      justify-content: center
      height: 40vh
    &__edit-btn-wrapper
      display: flex
      justify-content: flex-end
      margin: 15px 0
</style>
